import type { OptimizerResult } from "@/types/feeds";
import type { DisplayPlayerProp } from "./types";

export interface OddsSlotContext {
  isOverUnderSelection: boolean;
  isOverSelection: boolean;
  isUnderSelection: boolean;
}

function resolvePrimaryBookValue(
  overValue: number | null,
  underValue: number | null,
  context: OddsSlotContext,
): number | null {
  if (context.isOverUnderSelection) {
    if (context.isOverSelection) {
      return overValue ?? null;
    }
    if (context.isUnderSelection) {
      return underValue ?? null;
    }
  }
  return overValue ?? underValue ?? null;
}

export function setSportsbookOddsForBook(
  prop: DisplayPlayerProp,
  bookId: string,
  overValue: number | null,
  underValue: number | null,
  context: OddsSlotContext,
) {
  const primaryValue = resolvePrimaryBookValue(overValue, underValue, context);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const p = prop as any;
  const overKey = `${bookId}_over_odds`;
  const underKey = `${bookId}_under_odds`;
  const primaryKey = `${bookId}_odds`;

  if (overValue != null && p[overKey] == null) {
    p[overKey] = overValue;
  }
  if (underValue != null && p[underKey] == null) {
    p[underKey] = underValue;
  }
  if (primaryValue != null && p[primaryKey] == null) {
    p[primaryKey] = primaryValue;
  }

  if (bookId === "underdog" && primaryValue != null && p.underdog_direct_odds == null) {
    p.underdog_direct_odds = primaryValue;
  }
  if (bookId === "sleeper" && primaryValue != null && p.sleeper_direct_odds == null) {
    p.sleeper_direct_odds = primaryValue;
  }
}

export function applySingleSideOdds(
  prop: DisplayPlayerProp,
  bookId: string,
  value: number | null,
  side: "OVER" | "UNDER",
  context: OddsSlotContext,
) {
  if (value == null) {
    return;
  }
  if (side === "OVER") {
    setSportsbookOddsForBook(prop, bookId, value, null, context);
  } else {
    setSportsbookOddsForBook(prop, bookId, null, value, context);
  }
}

interface ApplyActiveSportsbookOddsArgs {
  prop: DisplayPlayerProp;
  result: OptimizerResult;
  rowSportsbook: string;
  fantasyAmerican: number | null;
  fantasyOppositeAmerican: number | null;
  isOverSelection: boolean;
  isUnderSelection: boolean;
}

export function applyActiveSportsbookOdds({
  prop,
  result,
  rowSportsbook,
  fantasyAmerican,
  fantasyOppositeAmerican,
  isOverSelection,
  isUnderSelection,
}: ApplyActiveSportsbookOddsArgs): void {
  if (fantasyAmerican !== null) {
    switch (rowSportsbook) {
      case "prizepicks":
        prop.prizepicks_odds = fantasyAmerican;
        prop.prizepicks_over_odds = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        prop.prizepicks_under_odds = isUnderSelection
          ? fantasyAmerican
          : fantasyOppositeAmerican;
        break;
      case "underdog":
        prop.underdog_odds = fantasyAmerican;
        prop.underdog_direct_odds = fantasyAmerican;
        prop.underdog_over_odds = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        prop.underdog_under_odds = isUnderSelection
          ? fantasyAmerican
          : fantasyOppositeAmerican;
        break;
      case "sleeper":
        prop.sleeper_odds = fantasyAmerican;
        prop.sleeper_direct_odds = fantasyAmerican;
        prop.sleeper_over_odds = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        prop.sleeper_under_odds = isUnderSelection
          ? fantasyAmerican
          : fantasyOppositeAmerican;
        break;
      case "fliff":
        prop.fliff_odds = fantasyAmerican;
        break;
      case "draftkings":
        prop.draftkings_odds = fantasyAmerican;
        prop.draftkings_over_odds = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        prop.draftkings_under_odds = isUnderSelection
          ? fantasyAmerican
          : fantasyOppositeAmerican;
        break;
      case "fanduel":
        prop.fanduel_odds = fantasyAmerican;
        prop.fanduel_over_odds = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        prop.fanduel_under_odds = isUnderSelection
          ? fantasyAmerican
          : fantasyOppositeAmerican;
        break;
      case "betmgm":
        prop.betmgm_odds = fantasyAmerican;
        prop.betmgm_over_odds = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        prop.betmgm_under_odds = isUnderSelection
          ? fantasyAmerican
          : fantasyOppositeAmerican;
        break;
      case "caesars":
        prop.caesars_odds = fantasyAmerican;
        prop.caesars_over_odds = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        prop.caesars_under_odds = isUnderSelection
          ? fantasyAmerican
          : fantasyOppositeAmerican;
        break;
      case "circa":
        prop.circa_odds = fantasyAmerican;
        prop.circa_over_odds = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        prop.circa_under_odds = isUnderSelection
          ? fantasyAmerican
          : fantasyOppositeAmerican;
        break;
      case "fanatics":
        prop.fanatics_odds = fantasyAmerican;
        prop.fanatics_over_odds = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        prop.fanatics_under_odds = isUnderSelection
          ? fantasyAmerican
          : fantasyOppositeAmerican;
        break;
      case "hardrock":
        prop.hardrock_odds = fantasyAmerican;
        prop.hardrock_over_odds = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        prop.hardrock_under_odds = isUnderSelection
          ? fantasyAmerican
          : fantasyOppositeAmerican;
        break;
      case "betr":
        prop.betr_odds = fantasyAmerican;
        prop.betr_over_odds = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        prop.betr_under_odds = isUnderSelection
          ? fantasyAmerican
          : fantasyOppositeAmerican;
        break;
      case "novig":
        prop.novig_odds = fantasyAmerican;
        prop.novig_over_odds = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        prop.novig_under_odds = isUnderSelection
          ? fantasyAmerican
          : fantasyOppositeAmerican;
        break;
      case "prophetx":
        prop.prophetx_odds = fantasyAmerican;
        prop.prophetx_over_odds = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        prop.prophetx_under_odds = isUnderSelection
          ? fantasyAmerican
          : fantasyOppositeAmerican;
        break;
      case "dabble":
        prop.dabble_odds = fantasyAmerican;
        prop.dabble_over_odds = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        prop.dabble_under_odds = isUnderSelection
          ? fantasyAmerican
          : fantasyOppositeAmerican;
        break;
      case "pinnacle":
        prop.pinnacle_odds = fantasyAmerican;
        prop.pinnacle_over_odds = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        prop.pinnacle_under_odds = isUnderSelection ? fantasyAmerican : fantasyOppositeAmerican;
        break;
      case "kalshi":
        prop.kalshi_odds = fantasyAmerican;
        prop.kalshi_over_odds = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        prop.kalshi_under_odds = isUnderSelection ? fantasyAmerican : fantasyOppositeAmerican;
        break;
      case "polymarket":
        prop.polymarket_odds = fantasyAmerican;
        prop.polymarket_over_odds = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        prop.polymarket_under_odds = isUnderSelection ? fantasyAmerican : fantasyOppositeAmerican;
        break;
      case "rebet":
        prop.rebet_odds = fantasyAmerican;
        prop.rebet_over_odds = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        prop.rebet_under_odds = isUnderSelection ? fantasyAmerican : fantasyOppositeAmerican;
        break;
      case "draftkingspredictions":
        prop.draftkingspredictions_odds = fantasyAmerican;
        prop.draftkingspredictions_over_odds = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        prop.draftkingspredictions_under_odds = isUnderSelection ? fantasyAmerican : fantasyOppositeAmerican;
        break;
      case "kambi":
        prop.kambi_odds = fantasyAmerican;
        prop.kambi_over_odds = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        prop.kambi_under_odds = isUnderSelection ? fantasyAmerican : fantasyOppositeAmerican;
        break;
      case "betonline":
        prop.betonline_odds = fantasyAmerican;
        prop.betonline_over_odds = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        prop.betonline_under_odds = isUnderSelection ? fantasyAmerican : fantasyOppositeAmerican;
        break;
      case "propbuilder":
        prop.propbuilder_odds = fantasyAmerican;
        prop.propbuilder_over_odds = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        prop.propbuilder_under_odds = isUnderSelection ? fantasyAmerican : fantasyOppositeAmerican;
        break;
      case "bovada":
        prop.bovada_odds = fantasyAmerican;
        prop.bovada_over_odds = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        prop.bovada_under_odds = isUnderSelection ? fantasyAmerican : fantasyOppositeAmerican;
        break;
      default: {
        const oddsKey = `${rowSportsbook}_odds`;
        const overKey = `${rowSportsbook}_over_odds`;
        const underKey = `${rowSportsbook}_under_odds`;
        (prop as any)[oddsKey] = fantasyAmerican;
        (prop as any)[overKey] = isOverSelection ? fantasyAmerican : fantasyOppositeAmerican;
        (prop as any)[underKey] = isUnderSelection ? fantasyAmerican : fantasyOppositeAmerican;
        break;
      }
    }
  }
}
